import React, { FC } from "react";

import {
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
  faCalendar,
  faChevronDown,
  faClipboard,
  faClipboardCheck,
  faDownload,
  faEdit,
  faFileAlt,
  faFolder,
  faGavel,
  faGripVertical,
  faHandshake,
  faHistory,
  faHome,
  faInfoCircle,
  faLock,
  faMapMarkerAlt,
  faPaperclip,
  faSearch,
  faSignOutAlt,
  faSort,
  faSortDown,
  faSortUp,
  faSquare,
  faTimes,
  faTrash,
  faUpload,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from "@fortawesome/react-fontawesome";
import { SortingOption } from "@utils";
import { IconName } from "./IconName.type";

function ChevronDown() {
  return <FontAwesomeIcon icon={faChevronDown} data-test-id="chevron-down" />;
}
function Edit() {
  return <FontAwesomeIcon icon={faEdit} data-testid="Edit" />;
}
function Times() {
  return <FontAwesomeIcon icon={faTimes} data-testid="Times" />;
}
function InfoCircle() {
  return <FontAwesomeIcon icon={faInfoCircle} data-testid="InfoCircle" />;
}
function Drag() {
  return <FontAwesomeIcon icon={faGripVertical} data-testid="Drag" />;
}
function LeftArrow() {
  return <FontAwesomeIcon icon={faArrowLeft} data-testid="LeftArrow" />;
}
function RightArrow() {
  return <FontAwesomeIcon icon={faArrowRight} data-testid="RightArrow" />;
}
function DownArrow() {
  return <FontAwesomeIcon icon={faArrowDown} data-testid="DownArrow" />;
}
function UpArrow() {
  return <FontAwesomeIcon icon={faArrowUp} data-testid="UpArrow" />;
}
function Home() {
  return <FontAwesomeIcon icon={faHome} data-testid="Home" />;
}
function Lock() {
  return <FontAwesomeIcon icon={faLock} data-testid="Lock" />;
}
function SignOut() {
  return <FontAwesomeIcon icon={faSignOutAlt} data-testid="SignOut" />;
}
function Hammer() {
  return <FontAwesomeIcon icon={faGavel} data-testid="Hammer" />;
}
function Handshake() {
  return <FontAwesomeIcon icon={faHandshake} data-testid="Handshake" />;
}
const History: FC = () => (
  <FontAwesomeIcon icon={faHistory} data-testid="History" />
);
function ClipboardCheck() {
  return (
    <FontAwesomeIcon icon={faClipboardCheck} data-testid="ClipboardCheck" />
  );
}
function Clipboard() {
  return <FontAwesomeIcon icon={faClipboard} data-testid="Clipboard" />;
}
function File() {
  return <FontAwesomeIcon icon={faFileAlt} data-testid="File" />;
}
function Folder() {
  return <FontAwesomeIcon icon={faFolder} data-testid="Folder" />;
}
function Square() {
  return <FontAwesomeIcon icon={faSquare} data-testid="Square" />;
}
function Calendar() {
  return <FontAwesomeIcon icon={faCalendar} data-testid="Calendar" />;
}
function Paperclip() {
  return <FontAwesomeIcon icon={faPaperclip} data-testid="Paperclip" />;
}
function Download() {
  return <FontAwesomeIcon icon={faDownload} data-testid="Download" />;
}
function Trash() {
  return <FontAwesomeIcon icon={faTrash} data-testid="Trash" />;
}
function Upload() {
  return <FontAwesomeIcon icon={faUpload} data-testid="Upload" />;
}
function MapMarker() {
  return <FontAwesomeIcon icon={faMapMarkerAlt} data-testid="MapMarker" />;
}
function User() {
  return <FontAwesomeIcon icon={faUser} data-testid="User" />;
}
const Search: FC<Omit<FontAwesomeIconProps, "icon">> = (props) => {
  return <FontAwesomeIcon icon={faSearch} data-testid="Search" {...props} />;
};

interface ISort extends Omit<FontAwesomeIconProps, "icon"> {
  direction?: SortingOption;
}
const Sort: FC<ISort> = ({ direction, ...props }) => {
  const icon = !direction
    ? faSort
    : direction === "asc"
    ? faSortUp
    : faSortDown;
  return (
    <FontAwesomeIcon size="lg" icon={icon} {...props} data-testid="SortIcon" />
  );
};

// Custom icons
const Filter: FC<{ applied?: boolean; title?: string }> = ({
  applied = false,
  title,
}) => {
  const style: React.CSSProperties = { cursor: "pointer" };
  if (applied) {
    style.color = "#1093C7";
  }
  return (
    <svg
      style={style}
      fill="currentColor"
      height="16px"
      viewBox="0 0 32 28"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title}</title>
      <path d="M30 6.749h-28c-0.69 0-1.25 0.56-1.25 1.25s0.56 1.25 1.25 1.25v0h28c0.69 0 1.25-0.56 1.25-1.25s-0.56-1.25-1.25-1.25v0zM24 14.75h-16c-0.69 0-1.25 0.56-1.25 1.25s0.56 1.25 1.25 1.25v0h16c0.69 0 1.25-0.56 1.25-1.25s-0.56-1.25-1.25-1.25v0zM19 22.75h-6.053c-0.69 0-1.25 0.56-1.25 1.25s0.56 1.25 1.25 1.25v0h6.053c0.69 0 1.25-0.56 1.25-1.25s-0.56-1.25-1.25-1.25v0z"></path>
    </svg>
  );
};
const EditDocument = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
      <g>
        <path
          fill="currentColor"
          d="M18.5687 12.0601L16.6937 10.1851C16.6356 10.1265 16.5665 10.08 16.4904 10.0483C16.4142 10.0166 16.3325 10.0002 16.25 10.0002C16.1675 10.0002 16.0858 10.0166 16.0096 10.0483C15.9335 10.08 15.8644 10.1265 15.8063 10.1851L10 15.9976V18.7538H12.7563L18.5687 12.9476C18.6273 12.8895 18.6738 12.8204 18.7056 12.7442C18.7373 12.668 18.7536 12.5863 18.7536 12.5038C18.7536 12.4213 18.7373 12.3396 18.7056 12.2635C18.6738 12.1873 18.6273 12.1182 18.5687 12.0601ZM12.2437 17.5038H11.25V16.5101L14.375 13.3851L15.3687 14.3788L12.2437 17.5038ZM16.25 13.4976L15.2563 12.5038L16.25 11.5101L17.2437 12.5038L16.25 13.4976Z"
        />
        <path
          fill="currentColor"
          d="M8.75 17.5002H5V2.50021H10V6.25021C10.001 6.58144 10.133 6.89883 10.3672 7.13304C10.6014 7.36725 10.9188 7.49926 11.25 7.50021H15V8.75021H16.25V6.25021C16.2522 6.16807 16.2366 6.08642 16.2043 6.01089C16.1719 5.93536 16.1235 5.86774 16.0625 5.81271L11.6875 1.43771C11.6325 1.37667 11.5649 1.32829 11.4893 1.29592C11.4138 1.26355 11.3321 1.24795 11.25 1.25021H5C4.66877 1.25117 4.35139 1.38317 4.11717 1.61739C3.88296 1.8516 3.75096 2.16899 3.75 2.50021V17.5002C3.75096 17.8314 3.88296 18.1488 4.11717 18.383C4.35139 18.6173 4.66877 18.7493 5 18.7502H8.75V17.5002ZM11.25 2.75021L14.75 6.25021H11.25V2.75021Z"
        />
      </g>
    </svg>
  );
};

export const Icon = {
  Times,
  Download,
  Upload,
  Home,
  Calendar,
  Handshake,
  History,
  Square,
  LeftArrow,
  InfoCircle,
  Edit,
  EditDocument,
  Drag,
  MapMarker,
  RightArrow,
  DownArrow,
  UpArrow,
  Paperclip,
  Lock,
  SignOut,
  Sort,
  Hammer,
  ChevronDown,
  ClipboardCheck,
  Clipboard,
  File,
  Filter,
  Folder,
  Trash,
  User,
  Search,
};

export const IconNames = Object.keys(Icon) as IconName[];
